import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import { MagneticSpotlightButton } from "@/components/PremiumAnimations";

const navLinks = [
  { label: "Work", path: "/work" },
  { label: "Services", path: "/services" },
  {
    label: "Industries",
    path: "/industries",
    children: [
      { label: "FinTech & Banking", path: "/industries/fintech-banking" },
      { label: "Trading & Capital Markets", path: "/industries/trading-capital-markets" },
      { label: "Digital Banking", path: "/industries/digital-banking" },
      { label: "Payments", path: "/industries/payments" },
      { label: "Wealth Management", path: "/industries/wealth-management" },
      { label: "Crypto & Web3", path: "/industries/crypto-web3" },
      { label: "SaaS & Platforms", path: "/industries/saas-platforms" },
      { label: "AI & Automation", path: "/industries/ai-automation" },
      { label: "Mobile Apps", path: "/industries/mobile-apps" },
      { label: "Enterprise", path: "/industries/enterprise" },
    ],
  },
  { label: "Approach", path: "/approach" },
  {
    label: "Resources",
    path: "/resources",
    children: [
      { label: "Blog", path: "/blog" },
      { label: "ROI Calculator", path: "/resources/roi-calculator" },
      { label: "Tech Stack Quiz", path: "/resources/tech-quiz" },
    ],
  },
  { label: "About", path: "/about" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
  }, [location]);

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const isActive = (link) => {
    if (link.path === "/" && location.pathname === "/") return true;
    if (link.path !== "/" && location.pathname.startsWith(link.path)) return true;
    if (link.children) return link.children.some((c) => location.pathname.startsWith(c.path));
    return false;
  };

  return (
    <>
      <motion.nav
        data-testid="navbar"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0, 0, 0.2, 1] }}
        className={`fixed top-0 w-full z-50 transition-colors duration-300 ${scrolled
          ? "bg-[#0B1116]/90 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent"
          }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group overflow-visible">
              <img src="/venury_logo.png" alt="Venury Logo" className="h-[90px] md:h-[120px] w-auto object-contain scale-[1.8] origin-left transition-transform duration-300 group-hover:scale-[1.85]" />
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-8" ref={dropdownRef}>
              {navLinks.map((link) => (
                <div key={link.path} className="relative">
                  {link.children ? (
                    <button
                      data-testid={`nav-${link.label.toLowerCase()}`}
                      onClick={() => setOpenDropdown(openDropdown === link.label ? null : link.label)}
                      className={`flex items-center gap-1 font-mono text-[13px] uppercase tracking-widest transition-colors duration-200 ${isActive(link) ? "text-teal-bright" : "text-slate-400 hover:text-teal-bright"
                        }`}
                    >
                      {link.label}
                      <ChevronDown size={12} className={`transition-transform duration-200 ${openDropdown === link.label ? "rotate-180" : ""}`} />
                    </button>
                  ) : (
                    <Link
                      to={link.path}
                      data-testid={`nav-${link.label.toLowerCase()}`}
                      className={`font-mono text-[13px] uppercase tracking-widest transition-colors duration-200 link-underline ${isActive(link) ? "text-teal-bright" : "text-slate-400 hover:text-teal-bright"
                        }`}
                    >
                      {link.label}
                    </Link>
                  )}

                  {/* Dropdown */}
                  <AnimatePresence>
                    {link.children && openDropdown === link.label && (
                      <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 8 }}
                        transition={{ duration: 0.15 }}
                        className="absolute top-full left-0 mt-3 w-64 bg-[#0B1116]/95 backdrop-blur-xl border border-white/10 rounded-lg shadow-2xl overflow-hidden"
                      >
                        <div className="py-2">
                          <Link
                            to={link.path}
                            className="block px-4 py-2.5 text-sm text-slate-400 hover:text-teal-bright hover:bg-white/5 transition-colors duration-150 font-semibold"
                          >
                            All {link.label}
                          </Link>
                          <div className="h-px bg-white/5 mx-4 my-1" />
                          {link.children.map((child) => (
                            <Link
                              key={child.path}
                              to={child.path}
                              className="block px-4 py-2.5 text-sm text-slate-400 hover:text-teal-bright hover:bg-white/5 transition-colors duration-150"
                            >
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
              <MagneticSpotlightButton
                as={Link}
                to="/contact"
                data-testid="nav-cta-button"
                className="flex items-center gap-2 !px-6 !py-2.5 !text-sm !font-semibold rounded transition-colors duration-200"
                strength={0.15}
              >
                <span>Let's Talk</span>
                <ArrowUpRight size={14} />
              </MagneticSpotlightButton>
            </div>

            {/* Mobile Toggle */}
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden text-white p-2"
            >
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-[#0B1116]/98 backdrop-blur-xl pt-24 px-6 overflow-y-auto"
          >
            <div className="flex flex-col gap-6">
              {navLinks.map((link, i) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.06 }}
                >
                  <Link
                    to={link.path}
                    data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                    className={`font-heading text-2xl font-bold tracking-tight ${isActive(link) ? "text-teal-bright" : "text-white"
                      }`}
                  >
                    {link.label}
                  </Link>
                  {link.children && (
                    <div className="ml-4 mt-2 space-y-2">
                      {link.children.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          className="block text-base text-slate-400 hover:text-teal-bright transition-colors"
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                <Link
                  to="/contact"
                  data-testid="mobile-nav-cta"
                  className="inline-flex items-center gap-2 bg-teal-primary text-white font-semibold text-lg px-8 py-4 rounded mt-4"
                >
                  Let's Talk <ArrowUpRight size={18} />
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
